import { LoadingButton } from "@mui/lab";
import { Alert, Box, Skeleton } from "@mui/material";
import { Stack } from "@mui/system";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import getCountries from "../../network/get-countries";
import getProduct from "../../network/get-product";
import { getUserToken } from "../../utils/account/common";

interface LoaderProps {
  language: Language;
  productId: string | null;
  onComplete: (product: Product, isUserLoggedIn: boolean) => void;
}
/**
 * Intermediate load component
 * Its purpose is to validate product parameter
 * before sending user to the onboarding flow
 */
const Loader = ({ language, productId, onComplete }: LoaderProps) => {
  const { data: product } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      return await getProduct({ language, productId });
    },
  });

  /**
   * Warm up country cache to enable quicker load
   * of country selector
   */
  const { data: countries } = useQuery({
    queryKey: ["countries", language],
    queryFn: () => getCountries(language),
  });

  /**
   * Load usertoken to see if visitor is already logged in
   */
  const { data: userToken, isLoading: isUserTokenLoading } = useQuery({
    queryKey: ["usertoken"],
    queryFn: getUserToken,
  });

  /**
   * Fire complete event as side-effect to prevent
   * updating while rendering. Otherwise the parent component's
   * state might update while it renders.
   */
  useEffect(() => {
    const ready = product && countries && !isUserTokenLoading;
    if (ready) {
      onComplete(product.properties, !!userToken);
    }
  }, [product, countries, userToken, isUserTokenLoading, onComplete]);

  if (productId === null) {
    return <Alert severity="error">Missing productId parameter</Alert>;
  }

  if (product === null) {
    return (
      <Alert severity="error">
        Failed loading product with id <em>{productId}</em>
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Stack alignItems="center" mb={6} direction="column">
          <Skeleton variant="text" width="40%" height={37} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={20} />
        </Stack>
        <Stack spacing={2} mb={4}>
          <Skeleton height={56} sx={{ px: 2, py: 1 }} />
          <Skeleton height={56} sx={{ px: 2, py: 1 }} />
          <Skeleton height={56} sx={{ px: 2, py: 1 }} />
          <Skeleton height={56} sx={{ px: 2, py: 1 }} />
        </Stack>

        <Box sx={{ display: "flex", alignItems: "center" }} mb={2}>
          <Skeleton variant="circular" width={38} height={38} sx={{ mr: 2 }} />
          <Skeleton variant="text" width="60%" height={24} />
        </Box>
      </Box>
      <Stack mt={2} direction="row" justifyContent="center">
        <LoadingButton variant="contained" disabled fullWidth>
          Get started
        </LoadingButton>
      </Stack>
    </>
  );
};

export default Loader;
